export const MARKETPLACE_INDUSTRIES = [
  { value: "dental", label: "Dental" },
  { value: "legal", label: "Legal" },
  { value: "home_services", label: "Home Services" },
  { value: "physical_therapy", label: "Physical Therapy" },
  { value: "chiropractic", label: "Chiropractic" },
  { value: "medical", label: "Medical" },
  { value: "other", label: "Other" },
] as const;

export const MARKETPLACE_ROLES = [
  { value: "receptionist", label: "Receptionist" },
  { value: "patient_coordinator", label: "Patient Coordinator" },
  { value: "client_intake", label: "Client Intake Specialist" },
  { value: "appointment_setter", label: "Appointment Setter" },
  { value: "customer_service", label: "Customer Service" },
  { value: "sales", label: "Inbound Sales" },
] as const;

/** Channels / skills employers filter by on the marketplace browse page. */
export const MARKETPLACE_RESPONSIBILITIES = [
  { value: "live_chat", label: "Live Chat", channelKey: "aiChat" as const },
  { value: "phone_calls", label: "Phone Calls", channelKey: "call" as const },
  { value: "text_messages", label: "Text Messages", channelKey: "sms" as const },
  { value: "email", label: "Email", channelKey: "email" as const },
  { value: "browser_calls", label: "Voice (WebRTC)", channelKey: "talk" as const },
  {
    value: "video_calls",
    label: "Video Calls",
    settingKey: "talkAnamVideoEnabled" as const,
  },
  {
    value: "welcome_video",
    label: "Welcome Video",
    settingKey: "welcomeVideoEnabled" as const,
  },
  { value: "whatsapp", label: "WhatsApp", channelKey: "whatsapp" as const },
  { value: "messenger", label: "Messenger", channelKey: "messenger" as const },
] as const;

/** Integrations / tools filter (matches marketplaceSkills on Project). */
export const MARKETPLACE_TOOLS = [
  {
    value: "juvonno",
    label: "Juvonno",
    description: "Physical therapy & clinic EMR scheduling",
    trending: true,
    popular: true,
  },
  {
    value: "nexhealth",
    label: "NexHealth",
    description: "Dental & medical patient scheduling",
    trending: true,
    popular: true,
  },
  {
    value: "google_calendar",
    label: "Google Calendar",
    description: "Calendar availability & event booking",
    trending: true,
    popular: true,
  },
  {
    value: "calendly",
    label: "Calendly",
    description: "Self-serve meeting scheduling",
    trending: false,
    popular: true,
  },
  {
    value: "zoom",
    label: "Zoom",
    description: "Video meetings & demo scheduling",
    trending: true,
    popular: false,
  },
  {
    value: "dentrix",
    label: "Dentrix",
    description: "Dental practice management scheduling",
    trending: false,
    popular: true,
  },
] as const;

export type MarketplaceResponsibility =
  (typeof MARKETPLACE_RESPONSIBILITIES)[number]["value"];
export type MarketplaceTool = (typeof MARKETPLACE_TOOLS)[number]["value"];

export type MarketplaceIndustry = (typeof MARKETPLACE_INDUSTRIES)[number]["value"];
export type MarketplaceRole = (typeof MARKETPLACE_ROLES)[number]["value"];

export function marketplaceIndustryLabel(value: string | null | undefined): string {
  if (!value) return "General";
  return MARKETPLACE_INDUSTRIES.find((i) => i.value === value)?.label ?? value;
}

export function marketplaceRoleLabel(value: string | null | undefined): string {
  if (!value) return "AI Agent";
  return MARKETPLACE_ROLES.find((r) => r.value === value)?.label ?? value;
}

export function normalizeMarketplaceSkills(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input.filter((s): s is string => typeof s === "string" && s.trim().length > 0);
}

export function marketplaceToolLabel(value: string | null | undefined): string {
  if (!value) return "Tool";
  const normalized = value.toLowerCase().replace(/\s+/g, "_");
  return (
    MARKETPLACE_TOOLS.find(
      (t) => t.value === normalized || t.label.toLowerCase() === value.toLowerCase(),
    )?.label ?? value
  );
}

export function marketplaceResponsibilityLabel(value: string | null | undefined): string {
  if (!value) return "Responsibility";
  return (
    MARKETPLACE_RESPONSIBILITIES.find((r) => r.value === value)?.label ?? value
  );
}

/** Build /agents query string from home page search filters. */
export function buildAgentsSearchUrl(filters: {
  search?: string;
  category?: string;
  industry?: string;
  role?: string;
  jobTitles?: string[];
  responsibilities?: string[];
  tools?: string[];
  listingType?: "ALL" | "OFFICIAL" | "MOONLIGHT";
}): string {
  const params = new URLSearchParams();
  const term = filters.search?.trim();
  if (term) {
    params.set("search", term);
  }
  if (filters.category && filters.category !== "all") {
    params.set("category", filters.category);
  }
  if (filters.industry && filters.industry !== "all") {
    params.set("industry", filters.industry);
  }
  if (filters.role && filters.role !== "all") {
    params.set("role", filters.role);
  }
  if (filters.jobTitles?.length) {
    params.set("jobTitles", filters.jobTitles.join(","));
  }
  if (filters.responsibilities?.length) {
    params.set("responsibilities", filters.responsibilities.join(","));
  }
  if (filters.tools?.length) {
    params.set("tools", filters.tools.join(","));
  }
  if (filters.listingType && filters.listingType !== "ALL") {
    params.set("listingType", filters.listingType);
  }
  const qs = params.toString();
  return qs ? `/agents?${qs}` : "/agents";
}

/** Default EasyGPT / Inbound GPT site concierge widget (home page sidebar). */
export const LANDING_SITE_AGENT_PROJECT_ID = "cm0dv6jno0001xs1fhdsr1a2h";

/** SiteGlue.ai marketing concierge widget (Victoria / SiteGlue AI). */
export const SITEGLUE_LANDING_SITE_AGENT_PROJECT_ID = "cmq6khumu001y8tjcfge02rxd";

/**
 * Public Text Victoria FAB on siteglue.ai — AgentPhone SMS inbox visitors should message.
 * Kept as a marketing constant so the FAB does not pick up a stale voice/SMS line from DB.
 */
export const SITEGLUE_VICTORIA_TEXT_SMS_E164 = "+17194972292";

export function getEasyGptMarketplaceOrgId(): string | null {
  return process.env.CLERK_INBOUND_GPT_ORGANIZATION_ID?.trim() || null;
}

export function getSiteGlueOrganizationId(): string | null {
  return (
    process.env.NEXT_PUBLIC_CLERK_SITEGLUE_ORGANIZATION_ID?.trim() ||
    process.env.CLERK_SITEGLUE_ORGANIZATION_ID?.trim() ||
    null
  );
}

/** Concierge project for the current marketing host. */
export function getLandingSiteAgentProjectId(hostname?: string): string {
  const host =
    hostname ??
    (typeof window !== "undefined" ? window.location.hostname : "");
  if (host.includes("siteglue.ai")) {
    return SITEGLUE_LANDING_SITE_AGENT_PROJECT_ID;
  }
  return LANDING_SITE_AGENT_PROJECT_ID;
}

/** Default commission for moonlight agent clones (cents). Override later via admin. */
export const DEFAULT_MOONLIGHT_COMMISSION_CENTS = 2500;
